import React, { useState, useRef } from "react"
import { Form } from "react-bootstrap"

const PasswordInput = ({
    placeholder = "Enter Password",
    name = "password",
    onChange = null,
    value = "",
    errors = {},
    showError = false,
    isValid = false,
    isInvalid = false,
    onBlur = null,
    autoFocus = false,
    ...props
}) => {
    const [showPassword, setShowPassword] = useState(false)
    const inputRef = useRef(null)

    const togglePassword = () => {
        setShowPassword((prev) => !prev)
        // Keep focus on input after toggle
        setTimeout(() => inputRef.current?.focus(), 0)
    }

    // Prevent ANY hover/mouseenter from changing input type
    const preventHoverReveal = (e) => {
        e.preventDefault()
        e.stopPropagation()
        if (inputRef.current) {
            // Force the type attribute to stay correct at DOM level
            inputRef.current.setAttribute("type", showPassword ? "text" : "password")
        }
    }

    return (
        <React.Fragment>
            <div
                className={`d-flex align-items-stretch rounded ${isInvalid ? "border border-danger" : isValid ? "border border-success" : "border"}`}
                style={{ overflow: "hidden", backgroundColor: "#fff" }}
                onMouseEnter={preventHoverReveal}
                onMouseOver={preventHoverReveal}
            >
                <input
                    ref={inputRef}
                    type={showPassword ? "text" : "password"}
                    placeholder={placeholder}
                    name={name}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    autoFocus={autoFocus}
                    autoComplete="new-password"
                    data-lpignore="true"
                    data-form-type="other"
                    onMouseEnter={preventHoverReveal}
                    onMouseOver={preventHoverReveal}
                    style={{
                        flex: 1,
                        border: "none",
                        outline: "none",
                        padding: "6px 12px",
                        fontSize: "1rem",
                        lineHeight: "1.5",
                        backgroundColor: "transparent",
                        color: "#212529",
                        minWidth: 0,
                        WebkitTextSecurity: showPassword ? "none" : "disc",
                    }}
                />
                <button
                    type="button"
                    onClick={togglePassword}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault()
                            togglePassword()
                        }
                    }}
                    tabIndex={0}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    style={{
                        background: "#f8f9fa",
                        border: "none",
                        borderLeft: "1px solid #dee2e6",
                        padding: "0 12px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        outline: "none",
                        WebkitAppearance: "none",
                        MozAppearance: "none",
                    }}
                >
                    {showPassword ? <EyeIconClose /> : <EyeIconOpen />}
                </button>
            </div>
            {showError && (
                <Form.Control.Feedback
                    type="invalid"
                    className="d-block mb-3"
                >
                    {errors}
                </Form.Control.Feedback>
            )}
        </React.Fragment>
    )
}

// Eye open = password is currently HIDDEN (dots), click to SHOW
const EyeIconOpen = () => (
    <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ pointerEvents: "none" }}>
        <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z" />
        <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z" />
    </svg>
)

// Eye closed = password is currently VISIBLE, click to HIDE
const EyeIconClose = () => (
    <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ pointerEvents: "none" }}>
        <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z" />
        <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z" />
        <path d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z" />
    </svg>
)

export default PasswordInput